import {
  ActionIcon,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Pagination,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";

import { notifications } from "@mantine/notifications";
import { IconBrandProducthunt, IconEdit, IconPlus } from "@tabler/icons-react";
import { safeApiCall } from "api/common.service";
import { logError } from "api/diagnostic.service";
import { getSuppliers } from "api/supplier.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
} from "common/constants";
import { DEFAULT_PAGE_SIZE } from "components/layouts/consts";
import React, { useEffect, useState } from "react";
import { PaginatedSuppliers, Supplier } from "types/model.types";
import AdminSupplierEntryPage from "./AdminSupplierEntryPage";
import AdminSupplierProductCodePage from "./AdminSupplierProductCodePage";

const AdminSupplierPage: React.FC = () => {
  const apiFetchRef = React.useRef<boolean>(false);
  const [selectedSupplier, setSelectedSupplier] = useState<
    Supplier | undefined
  >(undefined);
  const [preview, setPreview] = useState(false);
  const [previewType, setPreviewType] = useState<"costing" | "prices">(
    "prices",
  );
  const [entries, setEntries] = useState<PaginatedSuppliers>({
    data: [],
    total: 0,
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [reloadTrigger, setReloadTrigger] = useState(new Date());
  const [loading, setLoading] = useState<boolean>(true);
  const [activePage, setActivePage] = useState(1);
  const [exportData, setExportData] = useState<Supplier | null>(null);
  const [supplierModelOpen, setSupplierModelOpen] = useState(false);
  const [supplierCodeModelOpen, setSupplierCodeModelOpen] = useState(false);

  useEffect(() => {
    if (apiFetchRef.current) return; // if already fetched, do not refetch
    apiFetchRef.current = true;
    setLoading(true);

    const fetchData = async () => {
      try {
        const response = await safeApiCall(
          getSuppliers(activePage, DEFAULT_PAGE_SIZE),
        );
        if (response) {
          setEntries(response);
        }
      } catch (err) {
        logError(err);
        notifications.show({
          message: MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Error,
        });
      } finally {
        apiFetchRef.current = false;
        setLoading(false);
      }
    };
    fetchData();
  }, [activePage, reloadTrigger]);

  const handlePrint = (recordId: string | number) => {
    setPreview(true);
  };

  const rows = entries.data.map((entry) => {
    return (
      <Table.Tr key={entry.id}>
        <Table.Td className="action-cell">
          <Group gap="xs" wrap="nowrap">
            <Tooltip label="Edit">
              <ActionIcon
                variant="subtle"
                color="blue"
                onClick={() => {
                  setSelectedSupplier(entry);
                  setSupplierModelOpen(true);
                }}
              >
                <IconEdit size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Supplier Codes">
              <ActionIcon
                variant="subtle"
                color="blue"
                onClick={() => {
                  setSelectedSupplier(entry);
                  setSupplierCodeModelOpen(true);
                }}
              >
                <IconBrandProducthunt size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Table.Td>
        <Table.Td>{entry.id}</Table.Td>
        <Table.Td className="money">{entry.total}</Table.Td>
        <Table.Td>{entry.supplierCode}</Table.Td>
        <Table.Td>{entry.supplierName}</Table.Td>
      </Table.Tr>
    );
  });
  const handleSupplierSaved = (saved: Supplier) => {
    setEntries((prev) => {
      const exists = prev.data.some((s) => s.id === saved.id);

      let newData;

      if (exists) {
        // UPDATE existing supplier
        newData = prev.data.map((s) => (s.id === saved.id ? saved : s));
      } else {
        // ADD new supplier
        newData = [saved, ...prev.data];
      }

      return {
        ...prev,
        data: newData,
        total: exists ? prev.total : prev.total + 1,
      };
    });

    // Close modal, clear selection, etc.
    setSelectedSupplier(undefined);
    setSupplierModelOpen(false);
  };
  return (
    <Stack>
      <Title order={2}>Suppliers</Title>
      {loading ? (
        <Center>
          <Loader />
        </Center>
      ) : (
        <>
          <Group justify="space-between" wrap="nowrap" mb="md">
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                setSelectedSupplier(undefined);
                setSupplierModelOpen(true);
              }}
            >
              Add Supplier
            </Button>
            <Group justify="flex-end" wrap="nowrap" mb="md">
              <Text size="sm" c="dimmed">
                Showing 30 items per page
              </Text>
              <Pagination
                total={Math.ceil(entries.total / DEFAULT_PAGE_SIZE)}
                value={activePage}
                onChange={setActivePage}
                siblings={1}
                boundaries={1}
              />
            </Group>
          </Group>
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ whiteSpace: "nowrap" }}>Actions</Table.Th>
                <Table.Th>Supplier #</Table.Th>
                <Table.Th className="money" title="Total number of products">
                  # Products
                </Table.Th>
                <Table.Th>Codes</Table.Th>
                <Table.Th style={{ minWidth: 600 }}>Name</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
          <Group justify="flex-end" wrap="nowrap" mb="md">
            <Pagination
              total={Math.ceil(entries.total / DEFAULT_PAGE_SIZE)}
              value={activePage}
              onChange={setActivePage}
              siblings={1}
              boundaries={1}
            />
          </Group>
        </>
      )}
      {/* main supplier modal */}
      <Modal
        opened={supplierModelOpen}
        onClose={() => setSupplierModelOpen(false)}
        title="Add | Edit Supplier"
        size="xl"
        centered
        closeOnEscape={false}
        withinPortal={true} // default, but explicit here
        zIndex={2001} // ensure it's above layout elements
      >
        <AdminSupplierEntryPage
          initial={selectedSupplier}
          onSaveComplete={(saved: Supplier) => {
            handleSupplierSaved(saved);
          }}
          onCancel={() => {
            setSelectedSupplier(undefined);
            setSupplierModelOpen(false);
          }}
        />
      </Modal>
      {/* supplier product code modal */}
      <Modal
        opened={supplierCodeModelOpen}
        onClose={() => setSupplierCodeModelOpen(false)}
        title="Add | Edit Supplier Codes"
        size="xl"
        fullScreen
        centered
        closeOnEscape={false}
        withinPortal={true} // default, but explicit here
        zIndex={2001} // ensure it's above layout elements
      >
        <AdminSupplierProductCodePage
          supplier={selectedSupplier}
          onSaveComplete={(saved: Supplier) => {
            handleSupplierSaved(saved);
            // Close modal, clear selection, etc.
            setSelectedSupplier(undefined);
            setSupplierCodeModelOpen(false);
          }}
          onCancel={() => {
            setSelectedSupplier(undefined);
            setSupplierCodeModelOpen(false);
          }}
        />
      </Modal>
    </Stack>
  );
};

export default AdminSupplierPage;
